import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { keysToCamel } from '../../helpers/stringCaseTransform'

export type SearchPayload = {
  query: string
  agentId: string
}

export type SearchResultLink = {
  title?: string
  link?: string
  url?: string
  snippet?: string
}

export type SearchResponse = {
  summary: string
  results: SearchResultLink[]
  search?: {
    id?: string
    name?: string
  }
  rawResponse?: string
}

export type ImagePayload = {
  prompt: string
  agentId: string
  aspectRatio?: string
}

export type ImageResponse = {
  image?: {
    id?: string
    status?: string
    outputUrls?: string[]
  }
  status?: string
  images?: string[]
  videos?: string[]
}

export type ImageStatusResponse = {
  status?: string
  images?: string[]
  videos?: string[]
}

export type VideoPayload = {
  prompt: string
  agentId: string
  generationMode?: string
}

export type VideoResponse = {
  video?: {
    id?: string
    status?: string
    outputUrls?: string[]
  }
  image?: {
    id?: string
    status?: string
    outputUrls?: string[]
  }
  status?: string
  videos?: string[]
  images?: string[]
}

export type VideoStatusResponse = {
  status?: string
  videos?: string[]
  images?: string[]
}

export type CodeHtmlPayload = {
  prompt: string
  html: string
  agentId: string
  files?: File[]
}

export type CodeHtmlResponse = {
  html?: string
  provider?: string
  modelSlug?: string | null
  providerSettingId?: string | null
  agentId?: string | null
}

export const createModeApiSlice = (baseApiUrl: string) =>
  createApi({
    reducerPath: 'api/mode',
    baseQuery: fetchBaseQuery({
      baseUrl: baseApiUrl,
    }),
    endpoints: (builder) => ({
      invokeSearch: builder.mutation<SearchResponse, SearchPayload>({
        query: ({ query, agentId }) => ({
          url: `/agents/${agentId}/search`,
          method: 'POST',
          headers: {
            'x-agent-id': agentId,
          },
          body: {
            query,
            agent_id: agentId,
          },
        }),
        transformResponse: (response: unknown) => keysToCamel(response) as SearchResponse,
      }),

      generateImage: builder.mutation<ImageResponse, ImagePayload>({
        query: ({ prompt, agentId, aspectRatio }) => ({
          url: `/agents/${agentId}/image`,
          method: 'POST',
          headers: {
            'x-agent-id': agentId,
          },
          body: {
            prompt,
            agent_id: agentId,
            ...(aspectRatio && aspectRatio !== 'auto'
              ? { aspect_ratio: aspectRatio }
              : {}),
          },
        }),
        transformResponse: (response: unknown) => keysToCamel(response) as ImageResponse,
      }),

      getImageStatus: builder.mutation<ImageStatusResponse, { id: string; agentId: string }>({
        query: ({ id, agentId }) => ({
          url: `/agents/${agentId}/image/${id}/status`,
          method: 'GET',
          headers: {
            'x-agent-id': agentId,
          },
        }),
        transformResponse: (response: unknown) => keysToCamel(response) as ImageStatusResponse,
      }),

      generateVideo: builder.mutation<VideoResponse, VideoPayload>({
        query: ({ prompt, agentId, generationMode }) => ({
          url: `/agents/${agentId}/video`,
          method: 'POST',
          headers: {
            'x-agent-id': agentId,
          },
          body: {
            prompt,
            agent_id: agentId,
            generation_mode: generationMode || '--mp4',
          },
        }),
        transformResponse: (response: unknown) => keysToCamel(response) as VideoResponse,
      }),

      getVideoStatus: builder.mutation<VideoStatusResponse, { id: string; agentId: string }>({
        query: ({ id, agentId }) => ({
          url: `/agents/${agentId}/video/${id}/status`,
          method: 'GET',
          headers: {
            'x-agent-id': agentId,
          },
        }),
        transformResponse: (response: unknown) => keysToCamel(response) as VideoStatusResponse,
      }),

      editCodeHtml: builder.mutation<CodeHtmlResponse, CodeHtmlPayload>({
        query: ({ prompt, html, agentId, files }) => {
          const formData = new FormData()
          formData.append('prompt', prompt)
          formData.append('html', html)
          formData.append('agent_id', agentId)
          for (const file of files || []) {
            formData.append('files', file)
          }

          return {
            url: `/agents/${agentId}/code/html-edit`,
            method: 'POST',
            headers: {
              'x-agent-id': agentId,
            },
            body: formData,
          }
        },
        transformResponse: (response: unknown) => keysToCamel(response) as CodeHtmlResponse,
      }),
    }),
  })
